import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | expired | failed
  const attempts = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setStatus("paid");
          // Refresh user to get updated subscription_tier
          try {
            await api.get("/auth/me");
          } catch {}
          return;
        }
        if (["expired", "failed"].includes(data.status)) {
          setStatus(data.status);
          return;
        }
        if (attempts.current >= 15) {
          setStatus("timeout");
          return;
        }
        timerRef.current = setTimeout(poll, 2000);
      } catch {
        if (!cancelled) {
          setStatus("failed");
        }
      }
    };
    poll();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [sessionId]);

  return (
    <div className="max-w-lg mx-auto py-20 text-center">
      {status === "polling" && (
        <>
          <Loader2 className="w-16 h-16 mx-auto text-primary animate-spin" />
          <h1 className="font-kannada-heading text-3xl mt-6">ಪಾವತಿ ಪರಿಶೀಲಿಸುತ್ತಿದೆ...</h1>
          <p className="text-muted-foreground mt-2">ದಯವಿಟ್ಟು ಈ ಪುಟವನ್ನು ಮುಚ್ಚಬೇಡಿ.</p>
        </>
      )}
      {status === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 className="w-16 h-16 mx-auto text-primary" />
          <h1 className="font-kannada-heading text-4xl mt-6">ಸ್ವಾಗತ, ಪ್ರೀಮಿಯಂ ಸದಸ್ಯ!</h1>
          <p className="font-kannada-body text-lg text-muted-foreground mt-3">
            ನಿಮ್ಮ ಸಬ್‌ಸ್ಕ್ರಿಪ್ಶನ್ ಸಕ್ರಿಯವಾಗಿದೆ. ಜಾಹೀರಾತು ರಹಿತ ಸುದ್ದಿಗಳನ್ನು ಆನಂದಿಸಿ.
          </p>
          <div className="mt-8 flex gap-3 justify-center">
            <Button asChild className="rounded-none" data-testid="go-home-btn">
              <Link to="/">ಮುಖಪುಟಕ್ಕೆ</Link>
            </Button>
          </div>
        </div>
      )}
      {(status === "expired" || status === "failed" || status === "timeout") && (
        <div data-testid="payment-failed">
          <XCircle className="w-16 h-16 mx-auto text-destructive" />
          <h1 className="font-kannada-heading text-3xl mt-6">ಪಾವತಿ ಪೂರ್ಣಗೊಳ್ಳಲಿಲ್ಲ</h1>
          <p className="text-muted-foreground mt-2">
            {status === "timeout"
              ? "ಪರಿಶೀಲನೆ ಸಮಯ ಮೀರಿತು. ಸ್ವಲ್ಪ ಸಮಯದ ನಂತರ ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ."
              : "ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ."}
          </p>
          <Button asChild variant="outline" className="rounded-none mt-6">
            <Link to="/subscribe">ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ</Link>
          </Button>
        </div>
      )}
    </div>
  );
}
